import { useState, useRef, useEffect } from 'react';
import { Send, Sparkles, UserCheck, Bot, FileText, MessageCircle, CalendarClock, CheckCircle2 } from 'lucide-react';
import { useClientSettings } from './settings';

type Message = {
  id: string;
  from: 'user' | 'ai' | 'firm';
  text: string;
  time: string;
  forwarded?: boolean;
};

const seed: Message[] = [
  { id: '1', from: 'ai', text: 'Hi Bashir! I can answer questions about your business, taxes, and recent files. What would you like to know?', time: '9:42 AM' },
  { id: '2', from: 'user', text: 'How much GST did I pay last quarter?', time: '9:43 AM' },
  { id: '3', from: 'ai', text: "In Q3 (Jan-Mar 2026) you paid $4,820 in GST. That's about 12% lower than Q2 - mostly because of the equipment purchase that included GST credits.", time: '9:43 AM' },
  { id: '4', from: 'user', text: 'Can you help me understand my super contributions for last year?', time: '9:45 AM' },
  { id: '5', from: 'ai', text: "I'm not 100% sure on this one - your super setup involves some specific employer arrangements. I've sent your question to Hamid at the firm so she can answer properly.", time: '9:45 AM', forwarded: true },
];

type TimelineEvent = {
  id: string;
  icon: 'file' | 'message' | 'deadline' | 'done';
  title: string;
  detail: string;
  time: string;
  actor: 'firm' | 'you' | 'system';
};

const timelineEvents: TimelineEvent[] = [
  { id: 't1', icon: 'message', title: 'Hamid sent you a message', detail: 'About your March P&L equipment purchase…', time: '2 hours ago', actor: 'firm' },
  { id: 't2', icon: 'file', title: 'New file shared', detail: 'Tax Return FY2024-25.pdf', time: 'Today, 10:14 AM', actor: 'firm' },
  { id: 't3', icon: 'done', title: 'You uploaded New equipment receipts', detail: 'Marked complete', time: 'Yesterday', actor: 'you' },
  { id: 't4', icon: 'deadline', title: 'Q3 BAS reminder', detail: 'Due in 12 days', time: '2 days ago', actor: 'system' },
  { id: 't5', icon: 'message', title: 'You asked about super contributions', detail: 'Forwarded to your accountant', time: '3 days ago', actor: 'you' },
  { id: 't6', icon: 'file', title: 'New file shared', detail: 'Profit & Loss - Mar 2026.pdf', time: '8 Apr', actor: 'firm' },
  { id: 't7', icon: 'file', title: 'New file shared', detail: 'BAS Q3 2026.pdf', time: '5 Apr', actor: 'firm' },
];

export default function HelpPage() {
  const { t } = useClientSettings();
  const [tab, setTab] = useState<'chat' | 'timeline'>('chat');
  const [messages, setMessages] = useState<Message[]>(seed);
  const [input, setInput] = useState('');
  const [thinking, setThinking] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' });
  }, [messages, thinking]);

  const send = (text: string) => {
    if (!text.trim()) return;
    const time = new Date().toLocaleTimeString('en-AU', { hour: 'numeric', minute: '2-digit', hour12: true });
    const userMsg: Message = { id: String(Date.now()), from: 'user', text: text.trim(), time };
    setMessages(prev => [...prev, userMsg]);
    setInput('');
    setThinking(true);

    setTimeout(() => {
      const lower = text.toLowerCase();
      const escalate = lower.includes('super') || lower.includes('audit') || lower.includes('legal');
      const reply: Message = escalate
        ? { id: String(Date.now() + 1), from: 'ai', text: "That's a question best answered by your accountant directly - I've forwarded it to Hamid. She'll get back to you shortly.", time, forwarded: true }
        : { id: String(Date.now() + 1), from: 'ai', text: "Based on your most recent figures, here's what I can see - happy to dig deeper if you'd like more detail.", time };
      setMessages(prev => [...prev, reply]);
      setThinking(false);
    }, 900);
  };

  return (
    <div className="flex h-[calc(100dvh-180px)] flex-col pt-2">
      <div>
        <h1 className="text-2xl font-bold text-foreground">{t('help')}</h1>
        <p className="mt-1 text-sm text-muted-foreground">{t('ask_anything')}</p>
      </div>

      {/* Tabs */}
      <div className="mt-4 flex rounded-2xl bg-muted p-1">
        <button
          onClick={() => setTab('chat')}
          className={`flex flex-1 items-center justify-center gap-1.5 rounded-xl px-2 py-2 text-xs font-medium transition-colors ${
            tab === 'chat' ? 'bg-card text-foreground shadow-sm' : 'text-muted-foreground'
          }`}
        >
          <MessageCircle className="h-3.5 w-3.5" />
          {t('chat')}
        </button>
        <button
          onClick={() => setTab('timeline')}
          className={`flex flex-1 items-center justify-center gap-1.5 rounded-xl px-2 py-2 text-xs font-medium transition-colors ${
            tab === 'timeline' ? 'bg-card text-foreground shadow-sm' : 'text-muted-foreground'
          }`}
        >
          <CalendarClock className="h-3.5 w-3.5" />
          {t('timeline')}
        </button>
      </div>

      {tab === 'timeline' ? (
        <div className="mt-4 flex-1 overflow-y-auto pb-4">
          <div className="relative space-y-4 ps-6">
            <span className="absolute inset-y-2 start-2 w-px bg-border" />
            {timelineEvents.map(ev => (
              <TimelineRow key={ev.id} event={ev} />
            ))}
          </div>
        </div>
      ) : (
      <>
      <div ref={scrollRef} className="mt-4 flex-1 space-y-3 overflow-y-auto pb-3">
        {messages.map(m => <MessageBubble key={m.id} msg={m} />)}
        {thinking && (
          <div className="flex items-end gap-2">
            <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-violet-500/15">
              <Bot className="h-4 w-4 text-violet-600 dark:text-violet-400" />
            </div>
            <div className="rounded-2xl rounded-bl-sm bg-muted px-4 py-2.5">
              <div className="flex gap-1">
                <span className="h-2 w-2 animate-bounce rounded-full bg-muted-foreground" style={{ animationDelay: '0ms' }} />
                <span className="h-2 w-2 animate-bounce rounded-full bg-muted-foreground" style={{ animationDelay: '150ms' }} />
                <span className="h-2 w-2 animate-bounce rounded-full bg-muted-foreground" style={{ animationDelay: '300ms' }} />
              </div>
            </div>
          </div>
        )}
      </div>

      <form
        onSubmit={e => { e.preventDefault(); send(input); }}
        className="flex items-center gap-2 rounded-2xl border border-border bg-card p-2 shadow-sm"
      >
        <input
          type="text"
          value={input}
          onChange={e => setInput(e.target.value)}
          placeholder={t('type_message')}
          className="flex-1 bg-transparent px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none"
        />
        <button
          type="submit"
          disabled={!input.trim()}
          className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary text-primary-foreground transition-colors hover:bg-primary/90 disabled:bg-muted disabled:text-muted-foreground"
        >
          <Send className="h-4 w-4" />
        </button>
      </form>
      </>
      )}
    </div>
  );
}

function MessageBubble({ msg }: { msg: Message }) {
  if (msg.from === 'user') {
    return (
      <div className="flex justify-end">
        <div className="max-w-[78%] rounded-2xl rounded-br-sm bg-primary px-4 py-2.5 text-sm text-primary-foreground">
          {msg.text}
          <p className="mt-1 text-[10px] opacity-70">{msg.time}</p>
        </div>
      </div>
    );
  }

  const isFirm = msg.from === 'firm';
  return (
    <div className="flex items-end gap-2">
      <div className={`flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full ${isFirm ? 'bg-emerald-500/15' : 'bg-violet-500/15'}`}>
        {isFirm
          ? <UserCheck className="h-4 w-4 text-emerald-600 dark:text-emerald-400" />
          : <Sparkles className="h-4 w-4 text-violet-600 dark:text-violet-400" />}
      </div>
      <div className="max-w-[78%]">
        <div className="rounded-2xl rounded-bl-sm bg-muted px-4 py-2.5 text-sm text-foreground">
          {msg.text}
        </div>
        <div className="mt-1 flex items-center gap-2 px-1">
          <span className="text-[10px] text-muted-foreground">{msg.time}</span>
          {msg.forwarded && (
            <span className="flex items-center gap-1 rounded-full bg-emerald-500/10 px-2 py-0.5 text-[10px] font-medium text-emerald-700 dark:text-emerald-400">
              <UserCheck className="h-2.5 w-2.5" />
              Sent to your accountant
            </span>
          )}
        </div>
      </div>
    </div>
  );
}

function TimelineRow({ event }: { event: TimelineEvent }) {
  const iconMap = {
    file: { Icon: FileText, color: 'bg-violet-500/15 text-violet-600 dark:text-violet-400' },
    message: { Icon: MessageCircle, color: 'bg-sky-500/15 text-sky-600 dark:text-sky-400' },
    deadline: { Icon: CalendarClock, color: 'bg-amber-500/15 text-amber-700 dark:text-amber-300' },
    done: { Icon: CheckCircle2, color: 'bg-emerald-500/15 text-emerald-600 dark:text-emerald-400' },
  };
  const { Icon, color } = iconMap[event.icon];
  return (
    <div className="relative flex gap-3">
      <div className={`absolute -start-[1.55rem] top-0 flex h-7 w-7 items-center justify-center rounded-full ring-4 ring-background ${color}`}>
        <Icon className="h-3.5 w-3.5" />
      </div>
      <div className="flex-1 rounded-2xl border border-border bg-card p-3.5">
        <p className="text-sm font-semibold text-foreground">{event.title}</p>
        <p className="mt-0.5 text-xs leading-relaxed text-muted-foreground">{event.detail}</p>
        <p className="mt-1.5 text-[11px] text-muted-foreground/70">{event.time}</p>
      </div>
    </div>
  );
}
